import { addDays } from 'date-fns/addDays';
import { addMonths } from 'date-fns/addMonths';
import { addQuarters } from 'date-fns/addQuarters';
import { addWeeks } from 'date-fns/addWeeks';
import { addYears } from 'date-fns/addYears';
import { startOfDay } from 'date-fns/startOfDay';
import { startOfMonth } from 'date-fns/startOfMonth';
import { startOfQuarter } from 'date-fns/startOfQuarter';
import { startOfWeek } from 'date-fns/startOfWeek';
import { startOfYear } from 'date-fns/startOfYear';
import { subDays } from 'date-fns/subDays';
import { subMonths } from 'date-fns/subMonths';
import { subQuarters } from 'date-fns/subQuarters';
import { subSeconds } from 'date-fns/subSeconds';
import { subWeeks } from 'date-fns/subWeeks';
import { subYears } from 'date-fns/subYears';
import type { BdTemporalUnit } from './units';
import type { BdIntervalRelativePart } from './relative-date-interval';

export interface BdIntervalRelativeJson {
  start: BdIntervalRelativePart;
  duration: BdIntervalRelativePart;
}

const VALID_TEMPORAL_UNITS: ReadonlySet<string> = new Set<BdTemporalUnit>([
  'year',
  'quarter',
  'month',
  'week',
  'day',
]);

function isRelativePart(value: unknown): value is BdIntervalRelativePart {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const obj: Record<string, unknown> = value as Record<string, unknown>;
  return (
    typeof obj['count'] === 'number' &&
    typeof obj['temporalUnit'] === 'string' &&
    VALID_TEMPORAL_UNITS.has(obj['temporalUnit'])
  );
}

export function isBdIntervalRelativeJson(
  value: unknown,
): value is BdIntervalRelativeJson {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const obj = value as Record<string, unknown>;
  return isRelativePart(obj['start']) && isRelativePart(obj['duration']);
}

interface PresetDefinition {
  start: BdIntervalRelativePart;
  duration: BdIntervalRelativePart;
  label: string;
}

const PRESETS: readonly PresetDefinition[] = [
  {
    start: { temporalUnit: 'year', count: 1 },
    duration: { temporalUnit: 'year', count: 1 },
    label: 'Letztes Jahr',
  },
  {
    start: { temporalUnit: 'month', count: 6 },
    duration: { temporalUnit: 'month', count: 6 },
    label: 'Letztes Halbjahr',
  },
  {
    start: { temporalUnit: 'quarter', count: 1 },
    duration: { temporalUnit: 'quarter', count: 1 },
    label: 'Letztes Quartal',
  },
  {
    start: { temporalUnit: 'month', count: 1 },
    duration: { temporalUnit: 'month', count: 1 },
    label: 'Letzter Monat',
  },
];

const PLURAL_MAP: Record<BdTemporalUnit, { singular: string; plural: string }> =
  {
    year: { singular: '1 Jahr', plural: '# Jahre' },
    quarter: { singular: '1 Quartal', plural: '# Quartale' },
    month: { singular: '1 Monat', plural: '# Monate' },
    week: { singular: '1 Woche', plural: '# Wochen' },
    day: { singular: '1 Tag', plural: '# Tage' },
  };

function formatDuration(part: BdIntervalRelativePart): string {
  const entry = PLURAL_MAP[part.temporalUnit];
  if (part.count === 1) {
    return entry.singular;
  }
  return entry.plural.replace('#', String(part.count));
}

function computeStartDate(
  start: BdIntervalRelativePart,
  referenceDate: Date,
): Date {
  let date: Date;
  switch (start.temporalUnit) {
    case 'year':
      date = startOfYear(subYears(referenceDate, start.count));
      break;
    case 'quarter':
      date = startOfQuarter(subQuarters(referenceDate, start.count));
      break;
    case 'month':
      date = startOfMonth(subMonths(referenceDate, start.count));
      break;
    case 'week':
      date = startOfWeek(subWeeks(referenceDate, start.count));
      break;
    case 'day':
      date = subDays(referenceDate, start.count);
      break;
  }
  return startOfDay(date);
}

function computeEndDate(
  startDate: Date,
  duration: BdIntervalRelativePart,
): Date {
  let date: Date;
  switch (duration.temporalUnit) {
    case 'year':
      date = addYears(startDate, duration.count);
      break;
    case 'quarter':
      date = addQuarters(startDate, duration.count);
      break;
    case 'month':
      date = addMonths(startDate, duration.count);
      break;
    case 'week':
      date = addWeeks(startDate, duration.count);
      break;
    case 'day':
      date = addDays(startDate, duration.count);
      break;
  }
  return subSeconds(startOfDay(date), 1);
}

const DE_DATE_FORMAT = new Intl.DateTimeFormat('de-DE', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

export function formatRelativeIntervalDisplay(
  interval: BdIntervalRelativeJson,
  referenceDate?: Date,
): string {
  for (const preset of PRESETS) {
    if (
      interval.start.temporalUnit === preset.start.temporalUnit &&
      interval.start.count === preset.start.count &&
      interval.duration.temporalUnit === preset.duration.temporalUnit &&
      interval.duration.count === preset.duration.count
    ) {
      return preset.label;
    }
  }

  const ref = referenceDate ?? new Date();
  const startDate = computeStartDate(interval.start, ref);
  const endDate = computeEndDate(startDate, interval.duration);

  const duration = formatDuration(interval.duration);
  const startStr = DE_DATE_FORMAT.format(startDate);
  const endStr = DE_DATE_FORMAT.format(endDate);

  return `${duration} (${startStr} - ${endStr})`;
}
